import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'

export default function PackageDetail() {
  const { slug } = useParams()
  const [packageData, setPackageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        setLoading(true)

        // Fetch all packages and find the match
        const response = await fetch('http://localhost:3001/api/packages?limit=100')

        if (!response.ok) {
          throw new Error('Failed to fetch packages')
        }

        const data = await response.json()

        // Find package by matching slug (case-insensitive)
        const foundPackage = data.docs.find(pkg => {
          const pkgSlug = (pkg.slug || '').toLowerCase().replace(/\s+/g, '-')
          const urlSlug = slug.toLowerCase().replace(/\s+/g, '-')
          return pkgSlug === urlSlug && pkg.status === 'published'
        })

        if (foundPackage) {
          setPackageData(foundPackage)
        } else {
          throw new Error('Package not found')
        }

        setError(null)
      } catch (err) {
        console.error('Error fetching package:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchPackage()
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-safari-green"></div>
          <p className="text-gray-600 mt-4">Loading package details...</p>
        </div>
      </div>
    )
  }

  if (error || !packageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-2xl mx-auto px-4">
          <div className="text-6xl mb-4">📦</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Package Not Found</h1>
          <p className="text-gray-600 mb-8">
            Sorry, we couldn't find the safari package you're looking for.
          </p>
          <Link
            to="/experiences"
            className="btn-primary inline-block"
          >
            View All Experiences
          </Link>
        </div>
      </div>
    )
  }

  const heroImage = packageData.featuredImage?.sizes?.hero?.url || packageData.featuredImage?.url

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImage})`,
            filter: 'brightness(0.6)'
          }}
        />
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 font-heading drop-shadow-lg">
            {packageData.title}
          </h1>
          <p className="text-xl md:text-2xl drop-shadow-lg">
            {packageData.tagline || packageData.overview?.substring(0, 150)}
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-4 gap-6 mb-12">
              <div className="bg-safari-light p-6 rounded-xl text-center border border-safari-green">
                <FontAwesomeIcon icon="fa-clock" className="w-8 h-8 text-safari-green mx-auto mb-3" />
                <h3 className="font-bold text-safari-green mb-2">Duration</h3>
                <p className="text-gray-700">{packageData.duration || 'Contact Us'}</p>
              </div>
              <div className="bg-safari-light p-6 rounded-xl text-center border border-safari-green">
                <FontAwesomeIcon icon="fa-chart-simple" className="w-8 h-8 text-safari-green mx-auto mb-3" />
                <h3 className="font-bold text-safari-green mb-2">Difficulty</h3>
                <p className="text-gray-700">{packageData.difficulty || 'Moderate'}</p>
              </div>
              <div className="bg-safari-light p-6 rounded-xl text-center border border-safari-green">
                <FontAwesomeIcon icon="fa-tag" className="w-8 h-8 text-safari-green mx-auto mb-3" />
                <h3 className="font-bold text-safari-green mb-2">Price</h3>
                <p className="text-gray-700">{packageData.price ? `From $${packageData.price}pp` : 'Contact Us'}</p>
              </div>
              <div className="bg-safari-light p-6 rounded-xl text-center border border-safari-green">
                <FontAwesomeIcon icon="fa-star" className="w-8 h-8 text-safari-green mx-auto mb-3" />
                <h3 className="font-bold text-safari-green mb-2">Rating</h3>
                <p className="text-gray-700">{packageData.rating || '4.8'} ⭐</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-safari-green mb-6">Safari Overview</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                {packageData.overview}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Itinerary Section */}
      {packageData.itinerary && packageData.itinerary.length > 0 && (
        <section className="py-16 bg-safari-light">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-safari-green font-heading">
                Day-by-Day Itinerary
              </h2>
              <div className="space-y-6">
                {packageData.itinerary.map((day, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="flex items-start gap-4">
                      <div className="bg-safari-green text-white rounded-full w-12 h-12 flex items-center justify-center font-bold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{day.title}</h3>
                        <p className="text-gray-700 leading-relaxed">{day.description}</p>
                        {day.activities && day.activities.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {day.activities.map((activity, i) => (
                              <span key={i} className="bg-safari-light text-safari-green px-3 py-1 rounded-full text-sm">
                                {activity}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Highlights Section */}
      {packageData.highlights && packageData.highlights.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-safari-green font-heading">
                Safari Highlights
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {packageData.highlights.map((highlight, index) => (
                  <div key={index} className="bg-safari-light p-6 rounded-xl">
                    <div className="flex items-start gap-4">
                      <div className="bg-safari-green text-white p-3 rounded-lg">
                        <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700 leading-relaxed">{highlight}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Inclusions & Exclusions */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* What's Included */}
              {packageData.included && packageData.included.length > 0 && (
                <div className="bg-white p-8 rounded-xl shadow-lg">
                  <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-circle-check" className="w-6 h-6" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {packageData.included.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What's Not Included */}
              {packageData.excluded && packageData.excluded.length > 0 && (
                <div className="bg-white p-8 rounded-xl shadow-lg">
                  <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-circle-xmark" className="w-6 h-6" />
                    What's Not Included
                  </h3>
                  <ul className="space-y-3">
                    {packageData.excluded.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <FontAwesomeIcon icon="fa-xmark" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {packageData.gallery && packageData.gallery.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-safari-green font-heading">
                Photo Gallery
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {packageData.gallery.map((image, index) => (
                  <div key={index} className="relative h-64 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <img
                      src={image.sizes?.card?.url || image.url}
                      alt={image.alt || `${packageData.title} gallery ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-safari-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center" style={{ border: '1px solid #24684f' }}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-safari-green font-heading">
              Ready to Book This Safari?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-700">
              Contact us to customize this itinerary, check availability, or get a detailed quote for your perfect Uganda adventure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-safari-gold text-white px-8 py-4 rounded-lg font-bold hover:bg-safari-green transition-all duration-300 shadow-lg text-lg w-full sm:w-auto"
              >
                Book This Safari
              </button>
              <Link
                to="/experiences"
                className="border-2 border-safari-green text-safari-green px-8 py-4 rounded-lg font-bold hover:bg-safari-green hover:text-white transition-all duration-300 shadow-lg text-lg w-full sm:w-auto"
              >
                View All Safaris
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={packageData.title}
      />
    </div>
  )
}
